import {React, classNames, useAsObservableSource, useLocalStore, observer, observable, runInAction, useEffect, pluralize} from '@api'
import { Divider, Slide, TextField } from '@material-ui/core'
import { CatalogItemsListStore, useCatalogItemsListStore } from './'
import { SecondaryButton, PrimaryIconButton } from 'centric-ui-components'
import { Close } from '@material-ui/icons'
import { action } from 'mobx'
import * as css from './AddCatalogItemSidebar.css'
import {isTagField, StoresInstance} from '@stores'
import {useVipLocalStore, VipLocalStore} from '@modules'
import { IUpsertInput, IVipTag } from '@stores/elasticsearch'

enum KeyCode {
  ENTER = 13
}

class AddCatalogItemSidebarStore extends VipLocalStore {
  constructor(stores: StoresInstance,public store: CatalogItemsListStore) {
    super(stores)
  }

  @observable value: string = ''
  @observable error
  @observable errorMessage: string = ''
  @observable isDuplicate = false

  @action cancel = () => {
    this.value = ""
    this.store.isCreating = false
    this.store.isUpdating = false
    this.isDuplicate = false
  }

  @action onChangeValue = (event) => {
    const { store: { catalogItems, catalogItemsListData } } = this
    this.value = event.target.value
    this.isDuplicate = _.some(catalogItems || catalogItemsListData, (item) => _.lowerCase(item.name) === _.lowerCase(this.value))
    this.error = null;
  }

  @action create = async () => {
    const { store: { library, assignableField, tagType, catalogField }, value, es } = this, { catalog: { mutations: { createCatalogTag } }} = this.stores,
      index = library?.index ?? (isTagField(assignableField) ? es.indexes.tag : null)

    if (!index) {
      console.warn(`No index to create to for `, {library, assignableField})
      return
    }

    let payload: IUpsertInput = {
      index: index, value: {}
    }
    switch (index) {
      case "vip-tag":
         const input: IVipTag = {
            name: value.trim(),
            type: tagType ?? assignableField?.field ?? catalogField?.def?.tagType,
            description: null,
            index
          }
        payload = {
          ...payload, value: input
        }
        await createCatalogTag(payload)
        await this.store.refetch()
        break;
      default:
        return
        break;
    }
    this.value = ""
  }

  @action update = async () => {
    const {store, store: {tagType, library: storeLibrary, activeEditItem, getTagAssignedCount, catalogField}, value,
      flexLayout: {activeCatalogRoot: {catalog, catalog: {componentStores: {itemsList: {library}}, catalogItemName, dialogs: {showConfirmationProductPopup}}, es: {indexes}}}} = this,
      tagPath = library?.assignTo ?? `attributes.${tagType}s`,
      {assignedItemIds, count} = await getTagAssignedCount(tagPath, activeEditItem),
      confirmMsg: string = `Change '${activeEditItem?.name}' to '${value}'?`,
      offlineItems = catalog.offlineItemsAssignedToLibrary(library, activeEditItem),
      totalCount = count + offlineItems.length

    showConfirmationProductPopup({
      message: count === 0 ? confirmMsg : `${confirmMsg} \nIt is currently assigned to ${totalCount} ${pluralize(catalogItemName, totalCount)}.`,
      show: true,
      operation: 'UPDATE_TAG',
      id: activeEditItem?._id,
      additionalData: {type: activeEditItem?.type ?? tagType ?? catalogField?.def?.tagType, index: storeLibrary?.index ?? (tagType ? indexes.tag : null), name: value, targetIds: assignedItemIds.map(item => item.id), targetIndex: catalog.index, tagPath},
      onSuccess: () => {
        runInAction(() => {
          store.activeEditItem = {...activeEditItem, name: value, action: "update"}
          store.isUpdating = false
          store.refetch()
        })
      }
    })
  }

  @action onKeyPress = async (event) => {
    const { value, create, update, store: { isCreating }, isDuplicate } = this
    if (event.which === KeyCode.ENTER && value && !isDuplicate ) {
      isCreating ? await create() : await update()
    }
  }
}
  
export const AddCatalogItemSidebar = observer(function AddCatalogItemSidebar() {
  const store = useCatalogItemsListStore(),
    {isCreating, isUpdating, label, activeEditItem, assignable, assignableField} = store,
    localStore = useVipLocalStore((ws) => new AddCatalogItemSidebarStore(ws, store)),
    {cancel, value, onChangeValue, onKeyPress, create, update, isDuplicate} = localStore

    if(assignable) {
      localStore.value = activeEditItem?.name
      store.assignable = false
    }

    useEffect(() => {
      if (!isCreating && !isUpdating) {
        localStore.value = ''
      }
    }, [isCreating, isUpdating])

  const error = isDuplicate,
      errorMessage = isDuplicate ? `'${value}' already exist in ${label} library` : ''

  return (<Slide direction={'up'} in={isCreating || isUpdating} mountOnEnter unmountOnExit>
    <div className={classNames(css.root)}>
      <div className={css.header}>
        <div className={css.title}>{isUpdating ? 'Edit' : 'New'} {label}</div>
        <PrimaryIconButton
          icon={<Close classes={{ root: css.icon }} />}
          onClick={cancel}
        />
      </div>
      <Divider />
      <div className={css.form}>
        <TextField
          onChange={onChangeValue}
          autoFocus
          required
          error={error}
          classes={{ root: css.input }}
          placeholder={`Enter ${label}...`}
          value={value}
          onKeyPress={onKeyPress}
          helperText={errorMessage}
        />
      </div>
      <div className={css.actions}>
        <SecondaryButton
          onClick={cancel}
        >
          Cancel
        </SecondaryButton>
        <SecondaryButton
          disabled={error || value == null || value.trim() == ''|| (isUpdating && activeEditItem?.name == value)}
          onClick={isUpdating ? update : create}>
          {isUpdating ? 'Update' : 'Create'}
        </SecondaryButton>
      </div>
    </div>
  </Slide>)
})



// WEBPACK FOOTER //
// ./src/modules/catalog/sidebar/CatalogItemsList/AddCatalogItemSidebar.tsx